import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import entries from './TimelineData';
import { track } from '../analytics';
import GalaxyView from './GalaxyView';
import './Projects.css';

const SHOWCASE_ROUTES = {
  MLPipelineShowcase: '/projects/sentinel',
  CodeCollab:         '/projects/codecollab',
  Lens:               '/projects/lens',
  Prism:              '/projects/prism',
  Currency:           '/projects/currency',
  Flux:               '/projects/flux',
};

const TYPE_LABELS = {
  project:   'Project',
  work:      'Work',
  education: 'Education',
};

/* ── Hex node point generator ─────────────────────────── */
const hexPoints = (cx, cy, r) =>
  Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i - 90);
    return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
  }).join(' ');

/* ── SVG Signal Wire timeline ─────────────────────────── */
const GitGraph = ({ markerTops }) => {
  const TRUNK_X    = 16;
  const FEAT_X     = 62;
  const Y_OFF      = 28;
  const HEX_R_FEAT = 7;   // projects are the star — large prominent node
  const HEX_R_MAIN = 6.5; // work/edu — visible but subordinate to feature nodes

  const maxY = markerTops.length
    ? Math.max(...markerTops.filter(n => n >= 0), 0) + 220
    : 600;

  const TYPE_COL = {
    project:   '#1a96a6',
    work:      '#DB3A34',
    education: '#FFD700',
  };

  const chargePath = `M ${TRUNK_X} 0 L ${TRUNK_X} ${maxY}`;

  return (
    <svg className="git-graph-svg" width="72" height={maxY} aria-hidden="true" overflow="visible">
      <defs>
        <linearGradient id="trunkGrad" gradientUnits="userSpaceOnUse"
          x1={TRUNK_X} y1={0} x2={TRUNK_X} y2={maxY}>
          <stop offset="0%"   stopColor="#0a5f6e" stopOpacity="0.05" />
          <stop offset="5%"   stopColor="#1a96a6" stopOpacity="0.80" />
          <stop offset="97%"  stopColor="#1a96a6" stopOpacity="0.80" />
          <stop offset="100%" stopColor="#0a5f6e" stopOpacity="0.20" />
        </linearGradient>

        {/* Single shared glow — used only on feature project nodes */}
        <filter id="nodeGlow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Charge animation glow — reduced stdDeviation for perf */}
        <filter id="chargeGlow" x="-300%" y="-300%" width="700%" height="700%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ── Double-rail trunk ── */}
      <line x1={TRUNK_X - 1.5} y1={0} x2={TRUNK_X - 1.5} y2={maxY}
        stroke="url(#trunkGrad)" strokeWidth="1" />
      <line x1={TRUNK_X + 1.5} y1={0} x2={TRUNK_X + 1.5} y2={maxY}
        stroke="url(#trunkGrad)" strokeWidth="1" />

      {/* ── Charge pulse — hidden on mobile via CSS ── */}
      <g className="charge-pulse" filter="url(#chargeGlow)">
        <circle r="2" fill="#0d6b7a" opacity="0.6">
          <animateMotion dur="6s" begin="-0.24s" repeatCount="indefinite" path={chargePath} />
        </circle>
        <circle r="3.2" fill="#5ccfdc" opacity="0.92">
          <animateMotion dur="6s" repeatCount="indefinite" path={chargePath} />
        </circle>
      </g>
      <g className="charge-pulse" filter="url(#chargeGlow)" opacity="0.38">
        <circle r="2.6" fill="#1a96a6">
          <animateMotion dur="6s" begin="-3s" repeatCount="indefinite" path={chargePath} />
        </circle>
      </g>

      {/* ── Feature nodes — PROMINENT: projects are the highlight ── */}
      {entries.map((entry, i) => {
        if (entry.branch !== 'feature') return null;
        const mt = markerTops[i];
        if (mt == null) return null;
        const y = mt + Y_OFF;
        const FORK_Y = y - 40;
        const bPath = `M ${TRUNK_X} ${FORK_Y} Q ${TRUNK_X - 8} ${y} ${FEAT_X} ${y}`;
        return (
          <g key={i}>
            <path
              d={bPath}
              fill="none"
              stroke="#1a96a6" strokeWidth="1.2" strokeOpacity="0.55"
              strokeLinecap="round"
            />
            <polygon
              className="git-hex-pulse"
              points={hexPoints(FEAT_X, y, HEX_R_FEAT + 3.5)}
              fill="none" stroke="#1a96a6" strokeWidth="0.7"
            />
            <polygon
              points={hexPoints(FEAT_X, y, HEX_R_FEAT)}
              fill="#1a96a6" filter="url(#nodeGlow)" opacity="0.94"
            />
          </g>
        );
      })}

      {/* ── Main nodes — quiet type markers, no glow ── */}
      {entries.map((entry, i) => {
        if (entry.branch !== 'main') return null;
        const mt = markerTops[i];
        if (mt == null) return null;
        const y   = mt + Y_OFF;
        const col = TYPE_COL[entry.type];
        return (
          <g key={i}>
            <polygon
              points={hexPoints(TRUNK_X, y, HEX_R_MAIN)}
              fill={col} opacity="1"
            />
          </g>
        );
      })}
    </svg>
  );
};


/* ── Main component ───────────────────────────────────── */
const Projects = () => {
  const cardRefs = useRef([]);
  const [markerTops,  setMarkerTops]  = useState([]);
  const [galaxyOpen,  setGalaxyOpen]  = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.openGalaxy) {
      setGalaxyOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    const measure = () =>
      setMarkerTops(cardRefs.current.map(el => el?.offsetTop ?? 0));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const handleCardClick = useCallback((entry) => {
    if (entry.showcase) {
      track.projectClick(entry.showcase);
      navigate(SHOWCASE_ROUTES[entry.showcase]);
    } else if (entry.link) {
      track.projectClick(entry.title);
      window.open(entry.link, '_blank', 'noopener,noreferrer');
    }
  }, [navigate]);

  return (
    <>
    <section id="projects" className="projects-section">

      <div className="projects-layout">

        {/* ── Git graph spine ── */}
        <div className="timeline" aria-hidden="true">
          <GitGraph markerTops={markerTops} />
          {entries.map((entry, i) => (
            <div
              key={i}
              className={`timeline-entry timeline-entry--${entry.type}`}
              style={{ top: `${(markerTops[i] ?? 0) + 28}px` }}
            >
              <span className="timeline-label">{entry.date}</span>
            </div>
          ))}
        </div>

        {/* ── Cards ── */}
        <div className="projects-content">
          <div className="projects-title-row">
            <h1 className="projects-title">
              <span className="projects-title-prompt">{'>'}</span> Timeline
            </h1>
            <div className="mode-toggle" role="group" aria-label="View mode">
              <button
                className="mode-toggle-tab mode-toggle-tab--active"
                disabled
                aria-pressed="true"
              >
                <svg width="11" height="10" viewBox="0 0 11 10" fill="none" aria-hidden="true">
                  <path d="M3.5 1L1 5L3.5 9" stroke="currentColor" strokeWidth="1.3"
                    strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7.5 1L10 5L7.5 9" stroke="currentColor" strokeWidth="1.3"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Timeline</span>
              </button>
              <button
                className="mode-toggle-tab mode-toggle-tab--galaxy"
                onClick={() => setGalaxyOpen(true)}
                aria-label="Open galaxy view"
                aria-pressed="false"
              >
                <svg
                  className="mode-toggle-galaxy-icon"
                  width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"
                >
                  <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.1" />
                  <circle cx="6.5" cy="6.5" r="1.8" fill="currentColor" />
                  <ellipse cx="6.5" cy="6.5" rx="9.5" ry="2.8"
                    stroke="currentColor" strokeWidth="0.75"
                    strokeDasharray="2.4 1.8"
                    transform="rotate(-32 6.5 6.5)"
                  />
                </svg>
                <span>Galaxy</span>
              </button>
            </div>
          </div>
          {entries.map((entry, i) => (
            <div
              key={i}
              className={`project-card project-card--${entry.type}${(entry.link || entry.showcase) ? ' clickable' : ''}`}
              ref={el => (cardRefs.current[i] = el)}
              onClick={() => handleCardClick(entry)}
              role={(entry.link || entry.showcase) ? 'button' : undefined}
              tabIndex={(entry.link || entry.showcase) ? 0 : undefined}
              onKeyDown={e => e.key === 'Enter' && handleCardClick(entry)}
            >
              <div className="card-meta">
                <div className="card-meta-left">
                  <span className={`card-type card-type--${entry.type}`}>
                    {TYPE_LABELS[entry.type]}
                  </span>
                  {entry.live && (
                    <span className="card-live">
                      <span className="card-live-dot" aria-hidden="true" />
                      live
                    </span>
                  )}
                  {entry.status && <span className="card-status">{entry.status}</span>}
                </div>
                <div className="card-meta-right">
                  <span className="card-date">{entry.date}</span>
                  {entry.location && <span className="card-location">{entry.location}</span>}
                </div>
              </div>

              <div className="card-header">
                <h3 className="card-title">{entry.title}</h3>
                {entry.org && <span className="card-org">@ {entry.org}</span>}
              </div>

              {entry.branch === 'feature' && entry.what && entry.infra ? (
                <div className="card-dual">
                  <div className="card-dual-col">
                    <span className="card-dual-label">what it does</span>
                    <p className="card-dual-text">{entry.what}</p>
                  </div>
                  <div className="card-dual-divider" />
                  <div className="card-dual-col">
                    <span className="card-dual-label">how it runs</span>
                    <p className="card-dual-text card-dual-text--infra">{entry.infra}</p>
                  </div>
                </div>
              ) : (
                <p className="card-desc">{entry.description}</p>
              )}

              {entry.tags?.length > 0 && (
                <div className="card-tags">
                  {entry.tags.map(tag => (
                    <span key={tag} className={`card-tag card-tag--${entry.type}`}>{tag}</span>
                  ))}
                </div>
              )}

              {(entry.showcase || entry.link) && (
                <span className="card-cta">
                  {entry.showcase ? 'Explore →' : 'View →'}
                </span>
              )}
            </div>
          ))}


        </div>
      </div>
    </section>

    {galaxyOpen && ReactDOM.createPortal(
      <GalaxyView
        onClose={() => setGalaxyOpen(false)}
        onStartClose={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'instant' })}
      />,
      document.body
    )}
    </>
  );
};

export default Projects;
